import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { reservoirAPI } from '@/api/reservoir';
import { InstanceState } from '@/common/util';
import { useClaim, useInstanceStatus } from '@/hooks/mainStore';
import useMainStore from '@/store/mainStore';

/**
 * Custom hook to fetch and manage Reservoir floor token data using TanStack Query.
 *
 * This hook will:
 * 1. Return cached data if available
 * 2. Fetch new data if not yet available or stale
 * 3. Provide loading and error states for components
 * 4. Allow forced refresh of the data
 *
 * @returns An object containing the floor token data, loading state, and refresh function
 */
export function useReservoirFloorToken() {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const setReservoirFloorToken = useMainStore((state) => state.setReservoirFloorToken);
  const { claim, isLoading: claimIsLoading } = useClaim();
  const instanceStatus = useInstanceStatus();

  /**
   * Fetches the floor token data from Reservoir API
   */
  const fetchFloorToken = async () => {
    // If studio instance is not available, we can't fetch
    if (!studioInstance || !claim) {
      return null;
    }

    // Return null if the instance is on sale currently
    if (instanceStatus === InstanceState.Live || instanceStatus === InstanceState.Loading) {
      return null;
    }

    // Step 1: Get floor prices from reservoir
    const floorPricesResponse = await reservoirAPI.getFloorPrices(
      studioInstance.publicData.contract.contractAddress,
      studioInstance.publicData.network,
    );

    if (!floorPricesResponse) {
      console.error('Failed to fetch floor prices');
      throw new Error('Failed to fetch floor prices');
    }

    // Step 2: Get all token IDs
    const tokenIds = Object.keys(floorPricesResponse);

    if (tokenIds.length === 0) {
      return null;
    }

    // Step 3: Get token metadata
    const tokenMetadataResponse = await reservoirAPI.getTokensMetadata(
      studioInstance.publicData.contract.contractAddress,
      tokenIds,
      studioInstance.publicData.network,
    );

    // Step 4: Filter tokens to match the asset name
    const assetName = studioInstance.publicData.asset.name.toLowerCase();
    const filteredTokenMetadata = tokenMetadataResponse.filter((token) =>
      token.token.name.toLowerCase().includes(assetName),
    );

    if (filteredTokenMetadata.length === 0) {
      return null;
    }

    // Step 5: Find the token with the lowest floor price
    const lowestFloorAsk = filteredTokenMetadata.reduce((min, current) => {
      return Math.min(min, current.market.floorAsk.price.amount.decimal);
    }, Infinity);

    const newTokenObject = filteredTokenMetadata.find(
      (token) => token.market.floorAsk.price.amount.decimal === lowestFloorAsk,
    );

    return newTokenObject ?? null;
  };

  // Check for all conditions to fetch floor token
  const isEnabled =
    !!studioInstance &&
    !!claim &&
    !claimIsLoading &&
    instanceStatus !== InstanceState.Live &&
    instanceStatus !== InstanceState.Loading;

  // Using TanStack Query for data fetching with automatic caching and deduplication
  // avoiding the need to manually implement caching logic in the store
  const {
    data: floorToken,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'reservoirFloorToken',
      studioInstance?.publicData.contract.contractAddress,
      studioInstance?.publicData.network,
      studioInstance?.publicData.asset.name,
    ],
    queryFn: fetchFloorToken,
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    retry: 1, // Only retry once on failure
  });

  // Update the store whenever we get new data from a component that uses this hook
  // Only needed so that txStep's executeStep can access this data
  useEffect(() => {
    setReservoirFloorToken(floorToken ?? null);
  }, [floorToken, setReservoirFloorToken]);

  return {
    floorToken,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'An error occurred') : null,
    refreshFloorToken: refetch,
  };
}

/**
 * @example
 * ```tsx
 * import { useReservoirFloorToken } from '@/hooks/useReservoirFloorToken';
 *
 * function MyComponent() {
 *   const { floorToken, isLoading, error, refreshFloorToken } = useReservoirFloorToken();
 *
 *   // Show loading state
 *   if (isLoading) return <Skeleton width={80} />;
 *
 *   // Show error state
 *   if (error) return <div>Error: {error}</div>;
 *
 *   // No floor token available
 *   if (!floorToken) return <div>No listings available</div>;
 *
 *   // Display floor price
 *   return (
 *     <div>
 *       <p>Floor Price: {floorToken.market.floorAsk.price.amount.decimal} {floorToken.market.floorAsk.price.currency.symbol}</p>
 *       <button onClick={refreshFloorToken}>Refresh</button>
 *     </div>
 *   );
 * }
 * ```
 */
